import { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { useOnboardingStore } from "../../store/onboardingSlice";
import { saveOnboardingStep } from "../../api/auth";
import { connectGoogleCalendar, type CalendarProvider } from "../../api/googlecalendar";
import { connectMicrosoftCalendar } from "../../api/microsoftcalendar";
import { trackOnboardingStepComplete } from "../../lib/analytics";

export default function CalendarStep() {
  const { setCurrentStep, setStepData, setSaving, saving } = useOnboardingStore();
  const [error, setError] = useState<string | null>(null);
  const [provider, setProvider] = useState<Exclude<CalendarProvider, null>>("google");

  const handleConnect = () => {
    setError(null);
    if (provider === "google") {
      connectGoogleCalendar("onboarding");
      return;
    }
    connectMicrosoftCalendar("onboarding");
  };

  const handleSkip = async () => {
    setError(null);
    setSaving(true);
    try {
      setStepData("calendar", "skipped");
      await saveOnboardingStep({ step: "calendar", step_status: "skipped" });
      trackOnboardingStepComplete(5, "calendar_connection", true);
      setCurrentStep(6);
    } catch {
      setError("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleContinue = async () => {
    setError(null);
    setSaving(true);
    try {
      await saveOnboardingStep({ step: "calendar", step_status: "complete" });
      trackOnboardingStepComplete(5, "calendar_connection", true);
      setCurrentStep(6);
    } catch {
      setError("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1 }}>Connect your calendar</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Sync your availability so customers only book when you're free.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 2,
          p: 3,
          mb: 3,
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <CalendarMonthIcon sx={{ fontSize: 40, color: "primary.main" }} />
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>Connect your calendar</Typography>
          <Typography variant="body2" color="text.secondary">
            Choose Google or Microsoft to sync bookings and availability.
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 180, ml: { xs: 0, sm: "auto" } }}>
          <InputLabel id="onboarding-calendar-provider-label">Calendar Provider</InputLabel>
          <Select
            labelId="onboarding-calendar-provider-label"
            label="Calendar Provider"
            value={provider}
            onChange={(e) => setProvider(e.target.value as Exclude<CalendarProvider, null>)}
            disabled={saving}
          >
            <MenuItem value="google">Google Calendar</MenuItem>
            <MenuItem value="microsoft">Microsoft Calendar</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          sx={{ flexShrink: 0 }}
          onClick={handleConnect}
          disabled={saving}
        >
          Connect
        </Button>
      </Box>

      <Box sx={{ display: "flex", gap: 1.5 }}>
        <Button
          variant="contained"
          onClick={handleContinue}
          disabled={saving}
          sx={{ flex: 1 }}
        >
          {saving ? <CircularProgress size={20} color="inherit" /> : "Continue"}
        </Button>
        <Button variant="text" onClick={handleSkip} disabled={saving}>
          Skip for now
        </Button>
      </Box>
    </Box>
  );
}
